import { PresetEnum } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { useFlowLayoutContext, useMultipleFloatBoxBlockContext } from '@lighthouse/shared'
import { AnimatePresence, LayoutGroup } from 'motion/react'
import React, { forwardRef, useCallback, useMemo, useRef } from 'react'

import { ContainerBlockProvider, useContainerBlockContext } from '../../context'
import { useCustomViewBlockContext } from '../../context/CustomViewContext'
import { EmptyContainer } from '../EmptyContainer'
import { PanelLayout } from '../PanelLayout'

interface ContainerLayoutWithPanelProps extends React.ComponentPropsWithoutRef<'div'> {
    currentView?: string
    disabled?: boolean
    node: FlowLayoutNode
    anchorId?: string
}
export const ContainerLayoutWithPanel = forwardRef<HTMLDivElement, ContainerLayoutWithPanelProps>(
    ({ disabled: propDisabled, currentView, node, children, style, anchorId, ...rest }, ref) => {
        const { isRootSyncNode, data, id, children: panelChildren } = node
        const currentPanel = useMemo(() => panelChildren?.find(v => v.id === currentView), [panelChildren, currentView])

        const { interactionProps, reference } = useMultipleFloatBoxBlockContext()
        const { disabled: globalDisabled } = useFlowLayoutContext()
        const disabled = propDisabled ?? globalDisabled
        const noChildren = useMemo(() => {
            return !panelChildren?.some(v => v.type !== 'overlay')
        }, [panelChildren])
        const { record } = useCustomViewBlockContext()

        const { scope: contextScope, parentPanelSmartAnimation: contextParentPanelSmartAnimation } = useContainerBlockContext()

        const scope = isRootSyncNode ? id : contextScope
        const currentPanelSmartAnimation =
            data?.animation?.panelChange?.effect.preset === PresetEnum.smart ? data.animation.panelChange : undefined
        const parentPanelSmartAnimation = useMemo(
            () =>
                contextParentPanelSmartAnimation ||
                (currentPanelSmartAnimation && {
                    containerId: id,
                    animation: currentPanelSmartAnimation
                }),
            [contextParentPanelSmartAnimation, currentPanelSmartAnimation, id]
        )

        const containerValue = useMemo(
            () => ({
                scope,
                animation: data?.animation,
                parentPanelSmartAnimation
            }),
            [scope, data?.animation, parentPanelSmartAnimation]
        )

        const Wrapper = currentPanelSmartAnimation ? LayoutGroup : React.Fragment
        const AnimatePresenceOrNull = currentPanelSmartAnimation ? AnimatePresence : React.Fragment
        const scopeKey = useMemo(() => {
            if (!record && !scope) {
                return undefined
            } else if (record && scope) {
                return `${scope}:${record.id}`
            }
            return scope || record?.id
        }, [record, scope])
        const layoutGroupProps = currentPanelSmartAnimation ? { id: `${scopeKey}:${id}` } : {}
        const AnimatePresenceOrNullProps = currentPanelSmartAnimation ? { mode: 'popLayout' as const } : {}

        const layerInViewOnceMapRef = useRef<Map<string, boolean>>(new Map())
        const layerInViewOnceKey = useMemo(() => `${scopeKey ?? 'default'}:${id}`, [id, scopeKey])

        const containerReference = useCallback(
            (node: HTMLDivElement | null) => {
                if (!currentPanel?.id) {
                    return
                }

                if (!node) {
                    return
                }
                reference(node)
            },
            [currentPanel?.id, reference]
        )
        const containerReferenceProps = useMemo(() => interactionProps.getReferenceProps(rest), [interactionProps, rest])

        const containerBlockProviderValue = useMemo(() => {
            return {
                ...containerValue,
                layerInViewOnce: {
                    ownerBlockId: id,
                    key: layerInViewOnceKey,
                    mapRef: layerInViewOnceMapRef
                }
            }
        }, [containerValue, id, layerInViewOnceKey])

        return (
            <ContainerBlockProvider value={containerBlockProviderValue}>
                {noChildren && !disabled && <EmptyContainer />}
                <Wrapper {...layoutGroupProps}>
                    <AnimatePresenceOrNull {...AnimatePresenceOrNullProps}>
                        {currentPanel && (
                            <PanelLayout
                                key={currentPanel.id}
                                ref={ref}
                                panelNode={currentPanel}
                                containerNode={node}
                                disabled={disabled}
                                containerStyle={style}
                                containerReference={containerReference}
                                containerReferenceProps={containerReferenceProps}
                                anchorId={anchorId}
                            >
                                {children}
                            </PanelLayout>
                        )}
                    </AnimatePresenceOrNull>
                </Wrapper>
            </ContainerBlockProvider>
        )
    }
)
